import { useMeetingState } from '@daily-co/daily-react';
import { datadogLogs } from '@datadog/browser-logs';
import { EVENT_NAMES, useAnalytics } from '@web-app/context/AnalyticsContext';
import { useAutoRecording } from '@web-app/context/ProviderCallContext/useAutoRecording';
import { useAutoTranscription } from '@web-app/context/ProviderCallContext/useAutoTranscription';
import { useTranscriptionPermissions } from '@web-app/context/ProviderCallContext/useTranscriptionPermissions';
import { useSaveAppointment } from '@web-app/hooks/mutations/session-notes/saveAppointment';
import { useAppointmentByRoomId } from '@web-app/hooks/queries/appointment-by-room-name';
import type { AppointmentResponse } from '@web-app/hooks/queries/appointment-by-room-name/types';
import { useDurationPolling } from '@web-app/hooks/useDurationPolling';
import { getTimeLimitMinutes } from '@web-app/utils/appointment/appointment-type.utils';
import { callLogContext } from '@web-app/utils/daily-client/call-log-context';
import {
  calculateSafeDuration,
  getEffectiveStartTime,
  isDevelopment,
} from '@web-app/utils/env.utils';
import { useRealTimeTranscriptEnabled } from '@web-app/utils/feature-flags/real-time-transcript';
import {
  clearActiveCallData,
  setActiveCallData,
} from '@web-app/utils/session-beacon';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ProviderCallContextValue } from './ProviderCallContext';
import { useConnectedParticipantIds } from './useConnectedParticipantIds';

interface UseProviderCallStateArgs {
  roomId: string;
  appointment: AppointmentResponse | undefined;
  isProvider: boolean;
}

/**
 * Internal hook that manages provider call state.
 *
 * This hook contains all the business logic from useOngoingAppointment,
 * organized into three logical groups:
 * - lifecycle: Appointment creation and participant tracking
 * - duration: Time calculation and manual editing
 * - ui: Modal state and user interactions
 *
 * Handles:
 * 1. Creating appointment record when both participants join
 * 2. Storing start time from AppointmentEntity.createdAt (from mutation response)
 * 3. Duration calculation and manual editing
 * 4. Time limit notifications
 *
 * Edge cases:
 * - Provider rejoin: UPSERT returns original createdAt, preserving start time
 * - No start time: Duration = 0
 * - Negative duration: Protected by calculateSafeDuration()
 *
 * IMPORTANT: All action functions are wrapped in useCallback to prevent
 * unnecessary re-renders of context consumers.
 *
 * @internal This hook is only exported for testing purposes.
 * Use useProviderCallContext() in application code.
 */
export function useProviderCallState({
  roomId,
  appointment,
  isProvider,
}: UseProviderCallStateArgs): ProviderCallContextValue {
  // === APPOINTMENT CREATION STATE ===
  const [momentBothParticipantsJoined, setMomentBothParticipantsJoined] =
    useState<Date | null>(null);

  // === DURATION TRACKING STATE ===
  const [calculatedDuration, setCalculatedDuration] = useState(0);
  const [editedDuration, setEditedDuration] = useState<number | null>(null);
  const [editReason, setEditReason] = useState<string | null>(null);
  const [timeLimitShown, setTimeLimitShown] = useState(false);
  const [showTimeLimitModal, setShowTimeLimitModal] = useState(false);

  const { trackEvent } = useAnalytics();

  // Consume Daily's participant count
  const participantIds = useConnectedParticipantIds(roomId);
  const participantCount = useMemo(
    () => participantIds.length,
    [participantIds],
  );

  const meetingState = useMeetingState();

  // Prevent user from accidentally navigating away
  useEffect(() => {
    const onBeforeUnload = (e: WindowEventMap['beforeunload']) => {
      e.preventDefault();
      e.returnValue = '';
    };
    if (meetingState === 'joined-meeting') {
      window.addEventListener('beforeunload', onBeforeUnload);
    }

    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [meetingState]);

  // Get appointment data for time limits and audit logging
  const { data: healthieAppointment } = useAppointmentByRoomId(roomId);

  // Enable auto-recording when provider + patient are present
  useAutoRecording({
    roomId,
    participantCount,
    isProvider,
  });

  // Grant transcription permissions to all participants
  useTranscriptionPermissions({
    isInMeeting: meetingState === 'joined-meeting',
  });

  // Auto-start real-time transcription when provider + patient are present
  const realTimeTranscriptEnabled = useRealTimeTranscriptEnabled();
  useAutoTranscription({
    roomId,
    participantCount,
    isProvider,
    enabled: realTimeTranscriptEnabled,
  });

  // Persist duration to DB every 2 min (covers crash/kill/network loss)
  useDurationPolling({
    roomId,
    startTime: momentBothParticipantsJoined,
    isActive:
      meetingState === 'joined-meeting' && momentBothParticipantsJoined !== null,
  });

  // === APPOINTMENT CREATION ===
  const {
    mutate: saveAppointment,
    isPending: isCreatingAppointment,
    isSuccess: isAppointmentCreated,
  } = useSaveAppointment({
    onSuccess: (data) => {
      /**
       * We set the start time as the time the "appointment" record
       * was created to handle the edge case where a provider leaves and rejoins.
       * This way it will set it based on the first time they joined.
       *
       * The UPSERT operation:
       * - First call: Creates record with createdAt timestamp
       * - Subsequent calls: Returns existing record with original createdAt
       *
       * Note that this API endpoint hits appointments that are in the local
       * posgres database. This is NOT the same as the Healthie appointment
       * returned by `useAppointmentByRoomId`.
       */
      if (data.createdAt) {
        setMomentBothParticipantsJoined(new Date(data.createdAt));

        datadogLogs.logger.info(
          'Appointment created, start time set',
          callLogContext('save-appointment', {
            roomId,
            participantType: 'provider',
            startTime: data.createdAt,
            appointmentId: data.id,
            healthieAppointmentId: data.healthieAppointmentId,
            participantCount,
            isFirstCreation: data.createdAt === data.updatedAt,
          }),
        );
      } else {
        // Fallback: use current time if createdAt missing
        setMomentBothParticipantsJoined(new Date());

        datadogLogs.logger.error(
          'createdAt missing, falling back to current time',
          callLogContext('save-appointment', {
            roomId,
            participantType: 'provider',
            appointmentId: data.id,
          }),
        );
      }
    },
  });

  // Trigger appointment creation when both participants join
  useEffect(() => {
    if (
      isProvider &&
      participantCount >= 2 &&
      !isAppointmentCreated &&
      appointment?.client_id &&
      appointment?.provider_id
    ) {
      datadogLogs.logger.info(
        'Triggering appointment creation',
        callLogContext('save-appointment', {
          roomId,
          participantType: 'provider',
          participantCount,
          healthieClientId: appointment.client_id,
          healthieProviderId: appointment.provider_id,
          healthieAppointmentId: appointment.healthie_id,
        }),
      );

      saveAppointment({
        dailyRoomName: roomId,
        healthieClientId: appointment.client_id,
        healthieProviderId: appointment.provider_id,
        healthieAppointmentId: appointment.healthie_id,
      });
    }
  }, [
    isProvider,
    participantCount,
    isAppointmentCreated,
    appointment?.client_id,
    appointment?.provider_id,
    appointment?.healthie_id,
    roomId,
    saveAppointment,
  ]);

  // === DURATION CALCULATION ===

  /**
   * Calculate current duration based on start time
   * Called when end call modal opens
   */
  const calculateDuration = useCallback(() => {
    if (!momentBothParticipantsJoined) {
      datadogLogs.logger.warn(
        'No start time available, setting duration to 0',
        callLogContext('duration-tracking', {
          roomId,
          participantType: 'provider',
        }),
      );
      setCalculatedDuration(0);
      return;
    }

    const sessionStartTime = getEffectiveStartTime(momentBothParticipantsJoined);
    const sessionEndTime = new Date();
    const minutes = calculateSafeDuration(sessionStartTime, sessionEndTime);

    setCalculatedDuration(minutes);

    datadogLogs.logger.info(
      'Duration calculated',
      callLogContext('duration-tracking', {
        roomId,
        participantType: 'provider',
        sessionStartTime: sessionStartTime.toISOString(),
        sessionEndTime: sessionEndTime.toISOString(),
        calculatedMinutes: minutes,
        isDevelopment,
      }),
    );
  }, [momentBothParticipantsJoined, roomId]);

  /**
   * Handle manual duration edit by provider
   * Requires reason for audit trail and compliance
   */
  const editDuration = useCallback(
    (minutes: number, reason: string) => {
      setEditedDuration(minutes);
      setEditReason(reason);

      // Audit trail for compliance
      const auditData = {
        roomId,
        appointmentId: healthieAppointment?.healthie_id,
        providerId: healthieAppointment?.provider_id,
        clientId: healthieAppointment?.client_id,
        initialDuration: calculatedDuration,
        editedDuration: minutes,
        reason,
        timestamp: new Date().toISOString(),
      };

      trackEvent(EVENT_NAMES.CLICK_END_SESSION_BUTTON, auditData);

      datadogLogs.logger.info(
        'Duration manually edited',
        callLogContext('duration-tracking', {
          participantType: 'provider',
          ...auditData,
        }),
      );
    },
    [
      calculatedDuration,
      healthieAppointment?.healthie_id,
      healthieAppointment?.provider_id,
      healthieAppointment?.client_id,
      roomId,
      trackEvent,
    ],
  );

  /**
   * Dismiss the time limit modal
   */
  const dismissTimeLimitModal = useCallback(() => {
    setShowTimeLimitModal(false);
  }, []);

  // === TIME LIMIT NOTIFICATIONS ===
  useEffect(() => {
    if (
      !momentBothParticipantsJoined ||
      timeLimitShown ||
      !healthieAppointment?.healthie?.appointment_type?.id
    ) {
      return;
    }

    const timeLimitMinutes = getTimeLimitMinutes(
      healthieAppointment.healthie.appointment_type.id,
    );

    if (!timeLimitMinutes) {
      datadogLogs.logger.info(
        'No time limit configured',
        callLogContext('duration-tracking', {
          roomId,
          participantType: 'provider',
          appointmentTypeId: healthieAppointment.healthie.appointment_type.id,
          appointmentTypeName:
            healthieAppointment.healthie.appointment_type.name,
        }),
      );
      return;
    }

    const now = Date.now();
    const startTimeMs = momentBothParticipantsJoined.getTime();
    const timeLimitMs = timeLimitMinutes * 60 * 1000;
    const targetTime = startTimeMs + timeLimitMs;
    const delay = targetTime - now;

    datadogLogs.logger.info(
      'Time limit notification scheduled',
      callLogContext('duration-tracking', {
        roomId,
        participantType: 'provider',
        startTime: momentBothParticipantsJoined.toISOString(),
        timeLimitMinutes,
        targetTime: new Date(targetTime).toISOString(),
        delayMs: delay,
        willShowImmediately: delay <= 0,
      }),
    );

    // If time limit already passed, show modal immediately
    if (delay <= 0) {
      setTimeLimitShown(true);
      setShowTimeLimitModal(true);
      return;
    }

    // Schedule modal to show at time limit
    const timer = setTimeout(() => {
      datadogLogs.logger.info(
        'Time limit reached, showing notification',
        callLogContext('duration-tracking', {
          roomId,
          participantType: 'provider',
          timeLimitMinutes,
          elapsedMinutes: (Date.now() - startTimeMs) / 1000 / 60,
        }),
      );
      setTimeLimitShown(true);
      setShowTimeLimitModal(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [
    momentBothParticipantsJoined,
    timeLimitShown,
    healthieAppointment?.healthie?.appointment_type?.id,
    healthieAppointment?.healthie?.appointment_type?.name,
    roomId,
  ]);

  // Set active call data for forced beacon + log when start time becomes available
  useEffect(() => {
    if (momentBothParticipantsJoined) {
      setActiveCallData({
        roomId,
        startTime: getEffectiveStartTime(momentBothParticipantsJoined).getTime(),
        healthieAppointmentId: healthieAppointment?.healthie_id,
      });

      datadogLogs.logger.info(
        'Start time available for duration tracking',
        callLogContext('duration-tracking', {
          roomId,
          participantType: 'provider',
          startTime: momentBothParticipantsJoined.toISOString(),
          healthieAppointmentId: healthieAppointment?.healthie_id,
          source: 'AppointmentEntity.createdAt from saveAppointment mutation',
        }),
      );
    }

    return () => {
      clearActiveCallData();
    };
  }, [momentBothParticipantsJoined, roomId, healthieAppointment?.healthie_id]);

  // === COMPUTED VALUES ===

  /**
   * Final duration to use (edited takes precedence over calculated)
   * Memoized to prevent unnecessary recalculations
   */
  const finalDuration = useMemo(
    () => editedDuration ?? calculatedDuration,
    [editedDuration, calculatedDuration],
  );

  /**
   * Whether we have a valid start time for duration tracking
   * Memoized to prevent unnecessary recalculations
   */
  const hasStartTime = useMemo(
    () => momentBothParticipantsJoined !== null,
    [momentBothParticipantsJoined],
  );

  // === RETURN ORGANIZED CONTEXT VALUE ===

  return useMemo(
    () => ({
      lifecycle: {
        momentBothParticipantsJoined,
        isCreatingAppointment,
        isAppointmentCreated,
        participantCount,
        hasStartTime,
      },
      duration: {
        calculatedDuration,
        editedDuration,
        editReason,
        finalDuration,
        calculateDuration,
        editDuration,
      },
      ui: {
        timeLimitShown,
        showTimeLimitModal,
        dismissTimeLimitModal,
      },
    }),
    [
      momentBothParticipantsJoined,
      isCreatingAppointment,
      isAppointmentCreated,
      participantCount,
      hasStartTime,
      calculatedDuration,
      editedDuration,
      editReason,
      finalDuration,
      calculateDuration,
      editDuration,
      timeLimitShown,
      showTimeLimitModal,
      dismissTimeLimitModal,
    ],
  );
}
